import React, { useState, useEffect, useCallback } from 'react';
import SampleCardsSection from './SampleCardsSection';
// Import components
import Calendar from './Calendar';
import StatusChart from './StatusChart';
// Import styles
import './SampleManager.css';

const SampleManager = ({ user }) => {
    const [samples, setSamples] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [dateFilter, setDateFilter] = useState(null);
    const [filteredSamples, setFilteredSamples] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [refreshCount, setRefreshCount] = useState(0);

    // Fetch samples data function (MODIFIED for new backend)
    const fetchSamples = useCallback(async () => {
        console.log('SampleManager: Starting fetchSamples from new endpoint');
        try {
            setIsLoading(true);
            const response = await fetch('https://service9004.szapfs.org/api/orders');
            
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            
            const data = await response.json();
            console.log('SampleManager: Fetched data successfully:', data.length, 'orders');
            
            // MODIFIED: Transform the new order data, including the new stage and fields
            const transformedData = data.map(order => {
                // Determine the current stage based on the latest available date
                let currentStage = "Order Received";
                if (order.sent_cargo) currentStage = "Sent Cargo";
                if (order.cargo_delivered) currentStage = "Cargo Delivered";

                const items = Array.isArray(order.Items) ? order.Items : [];
                
                let kitTypeSummary;
                if (items.length > 1) {
                    kitTypeSummary = `${items.length} Kits`;
                } else if (items.length === 1) {
                    kitTypeSummary = items[0].KitName || '1 Kit';
                } else {
                    kitTypeSummary = 'No Kits';
                }

                return {
                    id: order.OrderID, 
                    firstName: order.firstName,
                    lastName: order.lastName,
                    email: order.Email,
                    items: items,
                    kitType: kitTypeSummary,
                    
                    // ADDED: New fields for use in detail views or search
                    address: order.address,
                    outbound_url: order.outbound_url,

                    status: {
                        currentStage: currentStage, // Updated logic
                        stages: {
                            "Order Received": order.order_received,
                            "Sent Cargo": order.sent_cargo,
                            "Cargo Delivered": order.cargo_delivered, // New stage
                        }
                    }
                };
            });
            
            console.log('SampleManager: Setting samples state with', transformedData.length, 'samples');
            setSamples(transformedData);
            setError(null);
        } catch (err) {
            console.error("Error fetching samples:", err);
            setError("Failed to load samples. Please try again later.");
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Initial data fetch (No change needed here)
    useEffect(() => {
        fetchSamples();
    }, [fetchSamples]);

    // Refresh data function (No change needed here)
    const handleRefreshData = useCallback(() => {
        console.log('SampleManager: Refreshing data');
        setRefreshCount(prev => prev + 1);
        fetchSamples();
    }, [fetchSamples]);

    // Filter samples based on search term and date (No change needed here)
    // This will continue to work because we mapped the new data to the old structure
    useEffect(() => {
        let filtered = [...samples];
        
        if (searchTerm.trim() !== '') {
            const searchTermLower = searchTerm.toLowerCase();
            filtered = filtered.filter(sample => 
                (sample.id && sample.id.toLowerCase().includes(searchTermLower)) ||
                (sample.firstName && sample.firstName.toLowerCase().includes(searchTermLower)) ||
                (sample.lastName && sample.lastName.toLowerCase().includes(searchTermLower)) ||
                (sample.kitType && sample.kitType.toLowerCase().includes(searchTermLower)) ||
                (sample.address && sample.address.toLowerCase().includes(searchTermLower)) // ADDED: Search by address
            );
        }
        
        if (dateFilter) {
            filtered = filtered.filter(sample => {
                const filterDate = new Date(dateFilter);
                filterDate.setHours(0, 0, 0, 0);
                const filterTime = filterDate.getTime();
                
                const stages = sample.status.stages;
                
                return Object.values(stages).some(stageDate => {
                    if (!stageDate) return false;
                    const date = new Date(stageDate);
                    date.setHours(0, 0, 0, 0);
                    return date.getTime() === filterTime;
                });
            });
        }
        
        setFilteredSamples(filtered);
    }, [samples, searchTerm, dateFilter]);

    // No change needed in the JSX return
    return (
        <div className="samplemanager-container">
            {isLoading && samples.length === 0 ? (
                <div className="samplemanager-loading">Loading samples...</div>
            ) : error && samples.length === 0 ? (
                <div className="samplemanager-error">{error}</div>
            ) : (
                <>
                    <div className="samplemanager-container-left">
                        <div className="samplemanager-container-left-top">
                            <Calendar 
                                onDateSelect={setDateFilter} 
                                onSearch={setSearchTerm}
                                dateFilter={dateFilter}
                                searchTerm={searchTerm}
                                refreshTrigger={refreshCount}
                                onRefreshData={handleRefreshData}
                            />
                        </div>
                        <div className="samplemanager-container-left-bottom">
                            {/* 
                              UPDATED: Pass the correct props.
                              - 'samples' is the array of filtered orders.
                              - 'onRefreshData' is the callback to reload data after a save.
                            */}
                            <SampleCardsSection 
                                samples={filteredSamples}
                                onRefreshData={handleRefreshData}
                            />
                        </div>
                    </div>
                    <div className="samplemanager-container-right">
                        <StatusChart 
                            samples={samples}
                            filteredSamples={filteredSamples}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default SampleManager;